import { addVat } from "./vat";
import type { Cents } from "../types/money";
import type { OrderDiscount, OrderTotals } from "../types/order";

/** Kortingsbedrag (excl. btw) op het goederen-subtotaal; nooit meer dan het subtotaal. */
export function discountAmount(subtotalExcl: Cents, discount?: OrderDiscount | null): Cents {
  if (!discount || discount.value <= 0) return 0;
  const raw = discount.kind === "percent" ? Math.round((subtotalExcl * discount.value) / 100) : discount.value;
  return Math.max(0, Math.min(raw, subtotalExcl));
}

/**
 * Shipping is delivery-only (no pickup). The customer-facing tariffs mirror the
 * Glastotaal model (glastotaal.nl/leveringsinformatie): a base rate that depends
 * on whether the order contains glass, a per-order handling fee on glass
 * products, and a small-order surcharge under a value threshold. (Timmers' own
 * price list only sets the supplier minimum of €34,03, Art. 4.3 — not a staffel.)
 */
export interface ShippingConfig {
  /** Delivery for an order that contains glass (excl. BTW), Nederland. */
  glassShippingCents: Cents;
  /**
   * Glastransport naar België (excl. btw). Timmers rekent hiervoor een eigen, hoger tarief
   * (€ 42,50 tegen € 35,35 binnen Nederland, per 1-11-2026). Dat onderscheid bestond hier niet:
   * een Belgische glasorder werd tegen het Nederlandse tarief berekend, en dat scheelde ons
   * € 7,15 per order. Leeg of 0 → het Nederlandse tarief, zoals het hiervoor was.
   */
  glassShippingBeCents: Cents;
  /** Delivery for an order with only accessories, no glass (excl. BTW, NL). */
  articlesShippingCents: Cents;
  /** Delivery for an accessory-only order to België (excl. BTW). Pakket-tarief BE. */
  articlesShippingBeCents: Cents;
  /** Handling fee charged once per order on glass products (excl. BTW). */
  handlingGlassCents: Cents;
  /** Orders with goods value below this get the surcharge (excl. BTW). */
  smallOrderThresholdCents: Cents;
  /** Surcharge added when goods value is under the threshold (excl. BTW). */
  smallOrderSurchargeCents: Cents;
  /** Speciaal vacuümglas-transport (BENGglas) per order, Nederland (excl. BTW). */
  benglasShippingNlCents: Cents;
  /** Speciaal vacuümglas-transport (BENGglas) per order, België (excl. BTW). */
  benglasShippingBeCents: Cents;
  /**
   * Vanaf deze orderwaarde (excl. btw) is het vacuümglas-transport gratis. 0 = uit.
   *
   * Waarom dit bestaat: dat transport is een vast bedrag per bestelling (€ 195 NL / € 225 BE),
   * en op een ruit van een halve vierkante meter kost het evenveel als het glas zelf. In GA4
   * zag je dat terug — 76 bezoekers in de configurator, nul bestellingen (30 dagen,
   * 27-10-2026). Boven een drempel waar genoeg marge in zit, is iets verkopen beter dan niets.
   */
  benglasFreeShippingThresholdCents: Cents;
  /**
   * Vanaf deze orderwaarde (excl. btw) is het PAKKETtarief gratis. 0 = uit.
   *
   * Geldt bewust alleen voor een order zonder glas — het gewone verzendtarief dus
   * (€ 15 NL / € 20 BE). Glastransport (€ 35,35 / € 42,50) en het speciale vacuümglas-transport
   * blijven staan: dat zijn echte, veel hogere vervoerskosten en het vacuümglas heeft zijn eigen
   * drempel hierboven. Zonder die beperking zou "gratis vanaf € 100" ongemerkt ook € 35,35
   * glastransport weggeven.
   *
   * Waarom dit bestaat: van de toestemmende NL/BE-bezoekers gingen er in 30 dagen 112 naar de
   * winkelwagen en 6 naar de kassa (28-10-2026). Op een mandje van een paar euro is € 15
   * verzendkosten de rem, en een drempel geeft de klant een reden om er iets bij te leggen.
   * Het bedrag is een margekeuze en hoort daarom in de admin, niet in de code.
   */
  freeShippingThresholdCents: Cents;
  /**
   * Eigen tarief voor een order ZONDER glas waarin een artikel langer dan 2500 mm zit
   * (Ventistone-latten, U-profielen, platte strip). 0 = volg het glastransport-tarief.
   *
   * Die twee liepen altijd gelijk, en daarom rekende de code gewoon het glastarief. Taco wees er
   * op 31-10-2026 op dat dat toeval is: Timmers kan het transport van lange goederen los van
   * glas gaan prijzen. Met 0 verandert er niets; vul je iets in, dan telt dat vanaf dan.
   */
  longArticlesShippingCents: Cents;
  /** Idem voor België. 0 = volg `longArticlesShippingCents`, en die op zijn beurt het glastarief. */
  longArticlesShippingBeCents: Cents;
}

/** Glastotaal defaults: €40 glas + €15 handeling, €15 artikelen, <€70 → +€20; BENG NL €195/BE €225. */
export const DEFAULT_SHIPPING_CONFIG: ShippingConfig = {
  glassShippingCents: 4000,
  glassShippingBeCents: 4250,
  articlesShippingCents: 1500,
  articlesShippingBeCents: 2000,
  handlingGlassCents: 1500,
  smallOrderThresholdCents: 7000,
  smallOrderSurchargeCents: 2000,
  benglasShippingNlCents: 19500,
  benglasShippingBeCents: 22500,
  // Uit by default: de drempel is een commerciële keuze en hoort in de admin gezet te worden.
  benglasFreeShippingThresholdCents: 0,
  freeShippingThresholdCents: 0,
  // 0 = volg het glastransport-tarief, zoals het altijd was.
  longArticlesShippingCents: 0,
  longArticlesShippingBeCents: 0,
};

/**
 * Het glastransport-tarief voor één land, exclusief handelingskosten.
 *
 * Sinds 1-11-2026 rekent Timmers voor België meer dan voor Nederland. Dat onderscheid hoort op één
 * plek te staan: de stadspagina's en de levertijdenpagina toonden het Nederlandse bedrag ook aan
 * Vlaamse bezoekers, met een comment erbij dat glastransport NL/BE gelijk zou zijn (Thijs,
 * 26-10-2026). Een niet-ingevuld Belgisch tarief valt terug op het Nederlandse, zoals voorheen.
 */
export function glasTransportCents(cfg: ShippingConfig, country: "NL" | "BE" = "NL"): Cents {
  return country === "BE" && (cfg.glassShippingBeCents ?? 0) > 0 ? cfg.glassShippingBeCents : cfg.glassShippingCents;
}

/** Backwards-compatible flat fallback (Timmers transport minimum, Art. 4.3). */
export const DEFAULT_SHIPPING_EXCL_CENTS = 3403;

/**
 * Resolve the shipping cost (excl. BTW) for an order from its goods subtotal and
 * whether it contains glass. Glass orders pay delivery + handling; accessory-only
 * orders pay the lower delivery rate; any order under the value threshold pays the
 * small-order surcharge on top.
 *
 * BENGglas (vacuümglas) heeft een eigen speciaal transport per order (NL/BE-tarief),
 * bovenop het gewone tarief van de overige regels. Een order met alléén BENGglas
 * betaalt alleen het BENGglas-transport.
 */
export function resolveShippingCents(
  subtotalExcl: Cents,
  hasGlass: boolean,
  cfg: ShippingConfig = DEFAULT_SHIPPING_CONFIG,
  country: "NL" | "BE" = "NL",
  opts?: { hasBenglas?: boolean; hasOtherItems?: boolean; hasLongArticles?: boolean },
): Cents {
  // Glasorders: bezorging + handeling. Alleen-artikelen-orders: het pakket-tarief, met een
  // apart (hoger) tarief voor België — en sinds 1-11-2026 geldt dat ook voor glastransport.
  const articles = country === "BE" ? (cfg.articlesShippingBeCents ?? cfg.articlesShippingCents) : cfg.articlesShippingCents;
  const hasBenglas = opts?.hasBenglas === true;
  // Alleen-BENGglas: geen glas-/pakketbasis. hasOtherItems dekt artikelen/roosters.
  const benglasOnly = hasBenglas && !hasGlass && opts?.hasOtherItems !== true;
  // Gratis verzending vanaf een drempel: alleen het pakkettarief, dus alleen een order zónder
  // glas. Glastransport en vacuümglas-transport zijn echte hoge vervoerskosten en blijven staan.
  const gratisPakket =
    cfg.freeShippingThresholdCents > 0 &&
    !hasGlass &&
    !benglasOnly &&
    opts?.hasLongArticles !== true &&
    subtotalExcl >= cfg.freeShippingThresholdCents;
  // Een order zónder glas maar mét een artikel langer dan 2500 mm: eigen tarief als dat gezet is,
  // anders het glastransport-tarief zoals voorheen. Géén glas-handelingskosten erbij — die horen
  // bij glas; bij de terugval blijven ze staan zodat er niets verandert zolang het tarief 0 is.
  const langTarief = country === "BE"
    ? (cfg.longArticlesShippingBeCents || cfg.longArticlesShippingCents)
    : cfg.longArticlesShippingCents;
  const langGoed = opts?.hasLongArticles === true && !hasGlass && !benglasOnly;
  const base = benglasOnly
    ? 0
    : hasGlass
      ? glasTransportCents(cfg, country) + cfg.handlingGlassCents
      : langGoed
        ? (langTarief > 0 ? langTarief : glasTransportCents(cfg, country) + cfg.handlingGlassCents)
        : gratisPakket
          ? 0
          : articles;
  const benglasTarief = country === "BE" ? cfg.benglasShippingBeCents : cfg.benglasShippingNlCents;
  // Boven de drempel vervalt het vacuümglas-transport. De drempel kijkt naar de héle order
  // (excl. btw), niet alleen naar het glas: wie er beslag bij bestelt, telt dat gewoon mee.
  const benglasGratis =
    cfg.benglasFreeShippingThresholdCents > 0 && subtotalExcl >= cfg.benglasFreeShippingThresholdCents;
  const benglas = hasBenglas && !benglasGratis ? benglasTarief : 0;
  const surcharge =
    !benglasOnly && !gratisPakket && subtotalExcl > 0 && subtotalExcl < cfg.smallOrderThresholdCents
      ? cfg.smallOrderSurchargeCents
      : 0;
  return base + benglas + surcharge;
}

/**
 * Order totals including delivery. BTW (21%) is charged over goods + shipping,
 * anchored on the excl. amounts (consistent with the rest of the engine).
 */
export function calculateOrderTotalsWithShipping(
  lineSubtotals: Cents[],
  shippingExcl: Cents,
  discount?: OrderDiscount | null,
  /** Btw-tarief; 0 bij een verlegde intracommunautaire levering. Default = het NL-tarief. */
  btwTarief?: number,
): OrderTotals {
  const subtotal = lineSubtotals.reduce((sum, line) => sum + line, 0);
  const disc = discountAmount(subtotal, discount);
  const { vat, total } = addVat(subtotal - disc + shippingExcl, btwTarief);
  return { subtotal, discount: disc > 0 ? disc : undefined, shipping: shippingExcl, vat, total };
}
